import { Bell, BellOff } from 'lucide-react';
import { useLanguage } from '../../../../features/language/useLanguage';

interface NotificationToggleCardProps {
  isEnabled: boolean;
  onToggle: () => void;
}

/** Master on/off switch controlling whether reminders are sent for new work orders. */
export function NotificationToggleCard({ isEnabled, onToggle }: NotificationToggleCardProps) {
  const { t } = useLanguage();
  const toggleLabel = t('notifications.settings.enableToggle', undefined, 'Send reminders for new work orders');

  return (
    <div className="px-4 sm:px-6 pt-5">
      <div
        className={`flex items-center justify-between gap-4 rounded-lg border p-4 transition-colors ${
          isEnabled ? 'border-primary-200 bg-primary-50/40' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
              isEnabled ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 text-gray-500'
            }`}
          >
            {isEnabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">{toggleLabel}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {isEnabled
                ? t('notifications.settings.statusEnabled', undefined, 'Reminders are active for new work orders.')
                : t('notifications.settings.statusDisabled', undefined, 'No reminders will be sent until enabled.')}
            </div>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isEnabled}
          aria-label={toggleLabel}
          onClick={onToggle}
          data-test-id="notification-enable-switch"
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            isEnabled ? 'bg-primary-600' : 'bg-gray-300'
          }`}
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              isEnabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    </div>
  );
}
